<template>
    <div class="page">
        <div class="content">
            <ContentBlock bgColor="#FFFFFF" id="topBlock">
                    <h3 class="topTitle">Case Study</h3>
                    <img class="logo" :src="require(`@/assets/shopLogo.png`)" />
            </ContentBlock>
            <ContentBlock bgColor="#FFFFFF" class="project-text">
                    <h4>Scale your prefab operations with one connected system</h4>
                    <p>EVOLVE Shop replaces scattered Excel sheets and email threads with a centralized, organized system for handling requests and execution. Standardize the workflow by defining the task, required activities, and deliverables. This ensures better tracking, communication, and consistency across all work.</p>
                    <div class="slideshow">
                        <div
                            class="slides"
                            :style="{ transform: `translateX(-${currentSlide * 100}%)` }"
                        >
                            <img
                                v-for="(slide, index) in slides"
                                :key="index"
                                :src="slide"
                            >
                        </div>

                        <button class="prev" @click="previousSlide">❮</button>
                        <button class="next" @click="nextSlide">❯</button>

                        <div class="slide-counter">
                            {{ currentSlide + 1 }} / {{ slides.length }}
                        </div>
                    </div>
            </ContentBlock>
            <ContentBlock bgColor="#0E0C0A" class="project-text">
                    <h3 class="white-text">The Challenge</h3>
                    <h4>Understanding the Problem</h4>
                    <p class="white-text">A common challenge across the construction industry is the lack of connectivity between the many platforms required to successfully deliver a project. This disconnect often creates communication gaps between key departments—including Design, Field, and Shop operations—resulting in inefficiencies that can place projects behind schedule before work even begins.<br><br>As EVOLVE continued to engage with fabrication teams, a major focus became understanding the challenges faced by Shop Foremen as they manage large crews, fragmented communication channels, and limited visibility into field operations.<br><br>This product explores the processes, tools, and workflows that enable high-performing fabrication shops to operate efficiently. By improving communication, increasing visibility, and streamlining coordination between teams, organizations can create a more connected and productive shop environment.</p>
            </ContentBlock>
            <ContentBlock bgColor="#FFFFFF" class="project-text">
                    <h3>The Process</h3>
                    <h4>Research Results</h4>
                    <p>Early in the development process, several lighthouse partners joined our team to provide insight into the real-world challenges facing the construction industry. One partner, in particular, played a critical role in helping us identify where to focus our initial efforts. They described their greatest challenge as the inability to effectively track a project's progress from start to finish. Existing software solutions lacked the ability to facilitate communication across departments, making it difficult to identify and address potential issues before they impacted project schedules.<br><br>Through these discussions, several innovative capabilities emerged that had not previously been available within construction software, including:</p>
                    <ul>
                        <li>Viewing work order information through Kanban board and calendar interfaces</li>
                        <li>Canceling work orders during production to accommodate rework processes</li>
                        <li>Adding attachments and comments to improve communication and clarity</li>
                        <li>Automatically synchronizing large volumes of data and files</li>
                        <li>Creating detailed location breakdown structures in minutes</li>
                        <li>Automatically routing tasks to designated work cells</li>
                        <li>Integrating with 3D modeling workflows and support tools</li>
                    </ul>
                    <p>The initial focus centered on improving shop operations, which partners consistently identified as the largest bottleneck within project delivery. Once these challenges were clearly defined, the team was able to develop solutions specifically designed to address the operational and communication gaps affecting fabrication workflows.</p>
            </ContentBlock>
            <ContentBlock bgColor="#FFFFFF" class="project-text">
                    <h4>Competitive Analysis</h4>
                    <p>The competitive landscape presented a unique opportunity, as no existing solution fully addressed the challenges faced by fabrication and construction teams. Many organizations relied on basic spreadsheet tools to manage highly complex project and production data, resulting in limited visibility, inefficient workflows, and disconnected processes.<br><br>Users expressed a clear need for a solution that would enable them to create work requests, convert those requests into actionable work orders, and track each work order throughout the entire production lifecycle. Providing end-to-end visibility and accountability became a key objective, allowing teams to monitor progress, improve communication, and maintain alignment from initial request through project completion.</p>
            </ContentBlock>
            <ContentBlock bgColor="#FFFFFF" class="project-text">
                    <h4>Excel</h4>
                    <div class="contentBlock-flex">
                        <p><strong>Strengths</strong><br>Excel spreadsheets can store unlimited rows of data which is highly beneficial to the user. When it comes to storing data of a high magnitude, Excel still remains the leader for this industry. The data is also able to be transferred to certain platforms.</p>
                        <p><strong>Weaknesses</strong><br>The user has to manually create a hierarchy for the information. Visually the information is not ideal to the user and can be difficult to make sense of. It is easy to make mistakes and overlook important information.</p>
                    </div>
                    <div class="contentBlock-flex">
                        <p><strong>Opportunities</strong><br>Excel was not created for the construction industry so it lacks in areas a new product can cater to specific user needs. The users are doing too much manual entry which is time consuming and leads to potential mistakes.</p>
                        <p><strong>Threats</strong><br>Most companies have continued using basic spreadsheets because there isn’t a competitor in the market. With EVOLVE, the tables were designed specifically to meet the needs of the industry.</p>
                    </div>
            </ContentBlock>
            <ContentBlock bgColor="#FFFFFF" class="project-text">
                <h4>User Personas</h4>
                <p>Meeting directly with users in their work environments provided valuable insight into the direction of the product. By observing designers and shop personnel using our solutions within their day-to-day operations, we gained a deeper understanding of their workflows, challenges, and dependencies. These observations allowed us to identify gaps in the user experience while also uncovering the additional tools and systems required to support their roles effectively.<br><br>Through ongoing collaboration with both a Design Manager and a Shop Foreman at a highly successful organization, we began to bridge the communication and workflow gap between the design and fabrication teams. Their perspectives helped shape a more connected approach, ensuring that the product addressed the needs of both departments and improved coordination throughout the project lifecycle.</p>
                <img :src="require(`@/assets/userPersona1.png`)" />
                <img :src="require(`@/assets/userPersona2.png`)" />
            </ContentBlock>
            <ContentBlock bgColor="#FFFFFF" class="project-text">
                <h4>User Stories</h4>
                <p>The lighthouse partners demonstrated remarkable consistency in their priorities and operational needs, making it easier to identify the shortcomings of existing solutions and uncover opportunities for innovation. Their alignment provided valuable insight into the features and capabilities that were most critical to project success.<br><br>One capability that consistently emerged as a high priority was the ability to interact directly with the 3D assembly model. For design users, access to and engagement with the model provided greater visibility, improved decision-making, and enhanced collaboration between design and downstream teams.</p>
                <img :src="require(`@/assets/userStoryChart.png`)" />
            </ContentBlock>
            <ContentBlock bgColor="#FFFFFF" class="project-text">
                <h4>User Flows</h4>
                <p>Beginning with the creation of a work order, this user flow illustrates the complete lifecycle of a work order from initiation through completion.<br><br>After a work order is created, users can divide it into multiple work packages, release tasks to production, and begin executing work within the production queue. As tasks progress through each stage of the workflow, users maintain visibility into status and completion, ultimately guiding the work order through to its successful completion.</p>
                <img :src="require(`@/assets/userFlows.png`)" />
            </ContentBlock>
            <ContentBlock bgColor="#0E0C0A" class="project-text">
                    <h3 class="white-text">The Framework</h3>
                    <h4>Low Fi Mockups</h4>
                    <p class="white-text">I created the designs for PDF Markups which customers had been asking for. Connecting with Autodesk Forma software, I recreated the tools within our own app so users could review, annotate and collaborate on drawings directly in work orders and tasks.</p>
                    <img :src="require(`@/assets/Lo\ Fi.png`)" />
            </ContentBlock>
            <ContentBlock bgColor="#0E0C0A" class="project-text">
                    <h4>Hi Fi Mockups</h4>
                    <p class="white-text">I connected closely with the front-end developer during creation and then customers to demo the tool and provide feedback before release. This was a game changing feature for users to communicate with their team for accuracy and consistency.</p>
                    <img :src="require(`@/assets/Hi\ Fi.png`)" />
                    <img :src="require(`@/assets/Hi\ Fi\ 2.png`)" />
            </ContentBlock>
            <ContentBlock bgColor="#0E0C0A" class="project-text">
                    <h4>Prototype</h4>
                    <p class="white-text">Building a prototype was an efficient way to communicate with developers and end users, to showcase the intention of the product before development.</p>
                    <div class="contentBlock-flex">
                        <video controls autoplay muted loop playsinline>
                            <source :src="require(`@/assets/PDFMarkupsTools.mp4`)"  type="video/mp4">
                            Your browser does not support the video tag.
                        </video>
                    </div>
            </ContentBlock>
            <ContentBlock bgColor="#0E0C0A" class="project-text">
                    <h4>Mobile Designs</h4>
                    <p class="white-text">Designed mobile for field foreman who need to submit and view work orders on the go. Delivery drivers will also utilize these for confirming delivery to the job site.</p>
                    <img class="mobileImage" :src="require(`@/assets/Mobile\ 1.png`)" />
                    <img class="mobileImage" :src="require(`@/assets/Mobile\ 2.png`)" />
                    <img class="mobileImage" :src="require(`@/assets/Mobile\ 3.png`)" />
            </ContentBlock>
            <ContentBlock bgColor="#FFFFFF" class="project-text">
                    <h3>The Impact</h3>
                    <p>As additional capabilities were introduced within EVOLVE Shop, customers became increasingly specific about their operational requirements and workflow needs. Features such as advanced filtering, the ability to pause or block work orders, and splitting work orders into multiple production tasks quickly emerged as essential functionality.<br><br>To further enhance the user experience, the platform introduced multiple methods for visualizing work order information, including project calendar and Kanban board views. These interfaces provided greater visibility into production status and scheduling. Additionally, the ability to attach photos and supporting documentation enabled users to communicate issues more effectively, improving collaboration and accelerating problem resolution across teams.</p>
            </ContentBlock>
            <ContentBlock bgColor="#0E0C0A" class="project-text">
                    <h3 class="white-text">The Reflection</h3>
                    <h4>Discovering a Solution</h4>
                    <p class="white-text">EVOLVE originated as a startup initiative within Applied Software and successfully evolved into an independent company in April 2022. This transition reflected the organization’s growth, market demand, and commitment to delivering innovative solutions for the construction industry.<br><br>From the outset, EVOLVE focused on creating a seamless user experience that supports the entire project lifecycle. The vision was to provide a centralized platform where teams could access critical information in one place while integrating with existing industry software to maximize efficiency and preserve established workflows.<br><br>EVOLVE recognizes the challenges Shop Foremen face as they coordinate activities across design, fabrication, and field teams. By connecting these departments through a unified platform, the company enables seamless communication and data sharing between Design, Shop, and Field operations. This integrated approach reduces rework, improves visibility, and creates more efficient workflows throughout the project lifecycle.</p>
            </ContentBlock>
            <ContentBlock bgColor="#0E0C0A" class="project-text">
                    <h4>What I Learned</h4>
                    <p class="white-text">The product being developed has the potential to significantly impact the construction industry by addressing challenges that have historically remained unresolved. Rather than entering the market as a direct competitor to existing solutions, the platform was designed to solve problems that had not previously been addressed through a unified approach. Its vision is to transform how construction teams collaborate, communicate, and manage operations across the project lifecycle.<br><br>Following the product’s launch, customer feedback generated an increasing number of feature requests and enhancement opportunities. This response reinforced the realization that existing solutions were not adequately meeting the needs of design, shop, and field teams. Despite the size and importance of the construction industry, significant opportunities for innovation remained largely untapped.<br><br>One of the greatest challenges in introducing a new solution has been driving organizational change and encouraging users to move beyond established, manual processes. Many teams have relied on the same methods for years, making adoption of new workflows a gradual process that requires both education and trust.<br><br>Continued engagement with users through feedback sessions and workplace observations has played a critical role in shaping the product’s evolution. These interactions have uncovered new pain points that would have otherwise remained hidden and have guided the development of interfaces and workflows tailored to real-world needs. While the platform continues to evolve, it has already addressed several major challenges within the construction industry and established a strong foundation for future innovation and process improvement.</p>
                    <img class="logo" id="bottomLogo" :src="require(`@/assets/evolveShop-white-logo.png`)" />
            </ContentBlock>
        </div>
        <Scroll />
    </div>
</template>

<script>
    import ContentBlock from '../components/ContentBlock.vue';
    import Scroll from "../components/Scroll.vue";

    export default {
        name: 'Shop',
        components: {
            ContentBlock,
            Scroll
        },
        data() {
            return {
                currentSlide: 0,
                slides: [
                    require('@/assets/slides/1.png'),
                    require('@/assets/slides/2.png'),
                    require('@/assets/slides/3.png'),
                    require('@/assets/slides/4.png'),
                    require('@/assets/slides/5.png'),
                    require('@/assets/slides/6.png'),
                    require('@/assets/slides/7.png'),
                    require('@/assets/slides/8.png')
                ]
            }
        },
        methods: {
            nextSlide() {
                this.currentSlide =
                    (this.currentSlide + 1) % this.slides.length
            },

            previousSlide() {
                this.currentSlide =
                    (this.currentSlide - 1 + this.slides.length) % this.slides.length
            }
        }
    }
</script>

<style scoped>
    .intro{
        text-align: center;
        font-weight: 700;
        margin-top: 50px;
        padding: 0px 30px;
       box-sizing: border-box;
    }

    #topBlock :deep(.contentBlock) {
        margin-bottom: 0;
    }

     h1{
        font-size: 3em;
        font-weight: 500;
    }

    h2{
        font-size: 2em;
        margin-top: 10px;
    }

    h3, h5{
        color: #0E0C0A;
        text-transform: uppercase;
    }

    h3{
        font-size: 1.3em;
    }

    h4{
       color: #C5B9A6; 
       font-size: 2em;
    }

    .white-text{
        color: #FFFFFF !important;
    }

    .topTitle{
       padding: 50px;
    }

    .content{
        margin: 100px 0px 30px 0px;
    }

    img.logo{
        max-width: 400px;
    }

    #bottomLogo{
        max-width: 200px;
        margin-top: 50px;
    }

    .project-text{
        padding: 50px;
    }

    video{
        width: 100%;
    }

    ul li{
        list-style-type: disc;
    }

    img.mobileImage{
        padding: 10px 100px;
        box-sizing: border-box;
    }

    .slideshow {
        position: relative;
        width: 100%;
        overflow: hidden;
    }

    .slides {
        display: flex;
        width: 100%;
        transition: transform .45s ease;
    }

    .slides img {
        flex: 0 0 100%;
        width: 100%;
        max-width: 100%;
        display: block;
    }

    .prev,
    .next {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        width: 44px;
        height: 44px;
        border: none;
        border-radius: 50%;
        background: rgba(0,0,0,.45);
        color: white;
        font-size: 24px;
        cursor: pointer;
    }

    .prev {
        left: 20px;
    }

    .next {
        right: 20px;
    }

    .prev:hover, .next:hover{
        background: rgba(0, 0, 0, 1);
        transition: 0.06s;
        cursor: pointer;
    }

    .slide-counter {
        position: absolute;
        bottom: 50px;
        left: 50%;
        transform: translateX(-50%);

        background: rgba(0, 0, 0, 0.6);
        color: #fff;

        padding: 8px 14px;
        border-radius: 20px;

        font-size: 14px;
        font-weight: 600;
        letter-spacing: 0.5px;
    }

    @media only screen and (max-width: 700px){
            h1{
                font-size: 2em;
            }

            h2{
                font-size: 1.3em;
                margin-top: 5px;
            }

            .project-text{
                padding: 30px;
            }

            img.mobileImage{
                padding: 10px 0px;
            }
    }
</style>
